const CENTRALISED_PROCEDURE_TYPES = new Set(
  ["Centralised Procedure", "Centralized Procedure"].map((value) => value.toLowerCase())
);

const EU_COUNTRIES = new Set(
  [
    "European Union / European Economic Area",
    "European Union",
    "EU",
    "EEA",
    "European Economic Area",
  ].map((value) => value.toLowerCase())
);

const EEA_MEMBER_COUNTRIES = ["Norway", "Iceland", "Liechtenstein"];

type RegistrationCountryRow = {
  procedureType?: string | null;
  country?: string | null;
};

export type RegistrationGroupRow = RegistrationCountryRow & {
  id?: string | number | null;
  groupId?: string | number | null;
  procedureNumber?: string | number | null;
};

export type CentralisedEeaCreateResult<T> =
  | { ok: true; siblings: T[] }
  | { ok: false; message: string };

const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? "";

const groupKey = (value?: string | number | null) =>
  value == null ? "" : String(value).trim();

const procedureKey = (value?: string | number | null) =>
  value == null ? "" : String(value).trim().toLowerCase();

const procedureLabel = (value?: string | number | null) =>
  value == null ? "" : String(value).trim();

const isCentralisedProcedure = (procedureType?: string | null) =>
  CENTRALISED_PROCEDURE_TYPES.has(normalize(procedureType));

export const isEuCentralisedRegistration = (row: RegistrationCountryRow) =>
  isCentralisedProcedure(row.procedureType) && EU_COUNTRIES.has(normalize(row.country));

export const isNonEuCentralisedRegistration = (row: RegistrationCountryRow) => {
  if (!isCentralisedProcedure(row.procedureType)) {
    return false;
  }
  return !EU_COUNTRIES.has(normalize(row.country));
};

const sameRegistration = (left: RegistrationGroupRow, right: RegistrationGroupRow) => {
  if (left === right) {
    return true;
  }
  return left.id != null && right.id != null && String(left.id) === String(right.id);
};

const countryRank = (country: string | null | undefined, selectedCountry: string) => {
  const name = normalize(country);
  if (name && name === selectedCountry) {
    return 0;
  }
  const memberIndex = EEA_MEMBER_COUNTRIES.findIndex(
    (member) => member.toLowerCase() === name
  );
  return memberIndex === -1 ? EEA_MEMBER_COUNTRIES.length + 1 : memberIndex + 1;
};

const procedurePairs = (rows: RegistrationGroupRow[], selectedCountry: string) => {
  const seen = new Set<string>();
  return [...rows]
    .sort(
      (left, right) =>
        countryRank(left.country, selectedCountry) - countryRank(right.country, selectedCountry)
    )
    .flatMap((row) => {
      const country = row.country?.trim() || "Unknown";
      const number = procedureLabel(row.procedureNumber);
      const key = `${country.toLowerCase()}|${number.toLowerCase()}`;
      if (seen.has(key)) {
        return [];
      }
      seen.add(key);
      return [`${country}: ${number}`];
    })
    .join("; ");
};

const missingCountryMessage = (
  countries: string[],
  groupId: string,
  procedureNumber: string
) =>
  `Cannot create xEVMPD. No matching registration found for ${countries.join(
    ", "
  )} with Group ID ${groupId} and Procedure Number ${procedureNumber}. The EU, Norway, Iceland, and Liechtenstein registrations must all match. No xEVMPD record was created.`;

const multipleProcedureMessage = (groupId: string, rows: RegistrationGroupRow[], selectedCountry: string) =>
  `Cannot create xEVMPD. Group ID ${groupId} has more than one Procedure Number. ${procedurePairs(
    rows,
    selectedCountry
  )}. One Group ID must have only one Procedure Number. Correct the registrations and try again.`;

export const validateCentralisedEeaCreate = <T extends RegistrationGroupRow>(
  selected: T,
  registrations: T[]
): CentralisedEeaCreateResult<T> => {
  const selectedGroupId = groupKey(selected.groupId);
  const selectedProcedure = procedureKey(selected.procedureNumber);
  const selectedCountry = normalize(selected.country);

  if (!selectedGroupId) {
    return {
      ok: false,
      message: missingCountryMessage(
        EEA_MEMBER_COUNTRIES,
        selectedGroupId,
        procedureLabel(selected.procedureNumber)
      ),
    };
  }

  const sameGroup = [
    selected,
    ...registrations.filter(
      (row) =>
        !sameRegistration(row, selected) &&
        isCentralisedProcedure(row.procedureType) &&
        groupKey(row.groupId) === selectedGroupId
    ),
  ];
  const procedureNumbers = new Set(sameGroup.map((row) => procedureKey(row.procedureNumber)));
  if (procedureNumbers.size > 1) {
    return {
      ok: false,
      message: multipleProcedureMessage(selectedGroupId, sameGroup, selectedCountry),
    };
  }

  const siblings = EEA_MEMBER_COUNTRIES.flatMap((country) => {
    const match = sameGroup.find(
      (row) =>
        normalize(row.country) === country.toLowerCase() &&
        procedureKey(row.procedureNumber) === selectedProcedure
    );
    return match ? [match] : [];
  });
  const missingCountries = EEA_MEMBER_COUNTRIES.filter(
    (country) => !siblings.some((row) => normalize(row.country) === country.toLowerCase())
  );
  if (missingCountries.length > 0) {
    return {
      ok: false,
      message: missingCountryMessage(
        missingCountries,
        selectedGroupId,
        procedureLabel(selected.procedureNumber)
      ),
    };
  }

  return { ok: true, siblings };
};

export const excludeNonEuCentralisedRegistrations = <T extends RegistrationCountryRow>(
  rows: T[]
) => rows.filter((row) => !isNonEuCentralisedRegistration(row));
